import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import TokensToolbar from "../TokensToolbar/TokensToolbar";
import './Footer.css'
import {selectIsTurnInputLocked} from "../../utils/gameState/turn/turnInputLock";
import {useFooterTitle} from "./useFooterTitle";
import BuildingHintsButton from "./BuildingHintsButton/BuildingHintsButton";
import {useShootingResultRevealed} from "../../utils/shooting/useShootingResultRevealed";
import UndoButton from "./UndoButton/UndoButton";
import SkipUnitTurnButton from "./SkipUnitTurnButton/SkipUnitTurnButton";
import PlanUndoButton from "./PlanUndoButton/PlanUndoButton";
import {checkIfConstructionPlanningAvailable} from "../../utils/gameState/construction/planning/getConstructionPlanContext";
import InfoButton from "./InfoButton/InfoButton";
import {CORNER_PANELS, useCornerPanels} from "../../hooks/visuals/useCornerPanels";
import {useFieldCornerControlsWidth} from "../../hooks/visuals/useFieldCornerControlsWidth";
import DeselectButton from "./DeselectButton/DeselectButton";
import InitiativeInterceptionButton from "./InitiativeInterceptionButton/InitiativeInterceptionButton";
import {useDispatch, useSelector} from "react-redux";
import {clearInspectedHex, deselectUnit, selectUnit, toggleDestructionTool} from "../../redux/actions";
import {useMeleeResultRevealed} from "../../utils/gameState/melee/useMeleeResultRevealed";
import {
  selectGameResults,
  selectHexes,
  selectIsAiMatch,
  selectIsParallelSupply,
  selectPendingMeleeDamage,
  selectPendingShootingActions,
  selectPhase,
  selectRound,
  selectUnits,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {checkIfUnitCouldManeuverFromHex} from "../../utils/gameState/maneuver/checkIfUnitCouldManeuverFromHex";
import {getActingPlayerFromState} from "../../utils/players/getActingPlayer";
import {checkIfParallelTurn} from "../../utils/gameState/turn/checkIfParallelTurn";
import {checkIfTurnWillBeAutoCompleted} from "../../utils/gameState/turn/executePhaseAutoActions";
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer
} from "../../utils/gameState/supply/parallelSupplySelectors";
import store from "../../redux/store";
import {GAME_PANEL_MATERIALS, GAME_PANEL_SHAPES, GamePanel} from "../gameUi/GamePanel";
import {GAME_BUTTON_SIZES, GAME_BUTTON_VARIANTS, GameButton} from "../gameUi/GameButton";
import GameEndContent from "../GameEndContent/GameEndContent";
import {addClassNameIfTrue} from "../../utils/other/addClassNameIfTrue";
import {checkIfAnyGamePreparationPhase} from "../../utils/checkIfAnyGamePreparationPhase";
import {usePrev} from "../../hooks/react/usePrev";
import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import CompletePhaseButton from "../Header/CompletePhaseButton/CompletePhaseButton";
import {useDesktopLayout} from "../../hooks/visuals/useWideScreenLayout";
import {useIsCurrentUserTurn} from "../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {
  selectDestructionToolActive,
  selectInspectedHexKey,
  selectIsFieldCutscenePlaying,
  selectIsTutorialActive,
  selectSelectedBarracksHexKey,
  selectSelectedUnitId
} from "../../redux/reducers/privateStateReducer/selectors";
import PolicyFanaticismConversionButton from "./PolicyFanaticismConversionButton/PolicyFanaticismConversionButton";
import ElementalsConstructionTokensOverlay
  from "./ElementalsConstructionTokensOverlay/ElementalsConstructionTokensOverlay";
import RecruitmentSummary from "./RecruitmentSummary/RecruitmentSummary";
import ConstructionPlanAutoCompleteTurnButton from "./ConstructionPlanAutoCompleteTurnButton/ConstructionPlanAutoCompleteTurnButton";
import {IS_CONSTRUCTION_PLAN_AUTO_COMPLETE_TURN_BUTTON_SHOWN} from "../../configs/constructionPlanAutoCompleteTurn";
import GiantsAutoSupplyToggle from "./GiantsAutoSupplyToggle/GiantsAutoSupplyToggle";
import ReactionsPanel from "../ReactionsPanel/ReactionsPanel";
import ChatPanel from "../ChatPanel/ChatPanel";
import {VisualToggles} from "../VisualToggles/VisualToggles";
import {selectMissionActiveReplica} from '../../redux/reducers/privateStateReducer/selectors';
import MissionEditorPanel from '../Mission/MissionEditorPanel/MissionEditorPanel';
import MissionReplicaContent from '../Mission/MissionReplica/MissionReplicaContent';
import {isEnvConfEnabled} from "../../utils/env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../../constants/env";
import {MASKED_ICON_TONES, MaskedIcon} from "../coloredIcons/MaskedIcon/MaskedIcon";
import {selectIsMatchChatEnabled} from "../../redux/reducers/sharedStateReducer/matchChatSelectors";

// Базальтовая плита футера со срезанными верхними углами. Лежит слоем позади
// содержимого, а не обрезает сам контейнер: clip-path на .footer-container
// срезал бы всплывающие над тулбарами подсказки и цены. Тень — фильтром на
// слое (у плиты срезаны углы), а не на футере: filter у предка сделал бы его
// контейнером для fixed-потомков
const FooterPlate = ({isBattle = false}) => (
  <div className={'footer-container_plate'} aria-hidden={true}>
    <GamePanel
      material={GAME_PANEL_MATERIALS.BAR}
      shape={GAME_PANEL_SHAPES.TOP}
      className={`footer-container_plate-face ${addClassNameIfTrue(isBattle, 'footer-container_plate-battle')}`}
    />
  </div>
);

export function Footer({isSpectatorMode = false}) {
  const dispatch = useDispatch();
  const isDesktopLayout = useDesktopLayout();
  const gameResults = useSelector(selectGameResults);
  // Реплика персонажа миссии показывается вместо тулбаров футера
  const activeReplica = useSelector(selectMissionActiveReplica);
  const destructionToolActive = useSelector(selectDestructionToolActive);
  const phase = useSelector(selectPhase);
  const warStage = useSelector(selectWarPhaseStage);
  const selectedUnitId = useSelector(selectSelectedUnitId);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const isConstructionPlanning = useSelector(checkIfConstructionPlanningAvailable);
  const isTutorialActive = useSelector(selectIsTutorialActive);
  const inspectedHexKey = useSelector(selectInspectedHexKey);
  // Осмотр ячейки на десктопе — плита по содержимому (см. Footer.css); пока идет реплика,
  // в футере она, а не панель осмотра
  const inspectingClassName = addClassNameIfTrue(Boolean(inspectedHexKey) && !activeReplica, 'footer-container_inspecting');
  // Заставка поля: облет захваченных городов в конце раунда (см.
  // scenes/CityCaptureAnimation) и рост дохода руин (scenes/RuinsIncomeAnimation).
  // Ход нового раунда уже начался, но футер с ним не показываем, — поле в это
  // время все равно не принимает ввод
  const isFieldCutscenePlaying = useSelector(selectIsFieldCutscenePlaying);
  const cityCaptureHiddenClassName = addClassNameIfTrue(isFieldCutscenePlaying, 'app-footer_city-capture-hidden');
  const selectedBarracksHexKey = useSelector(selectSelectedBarracksHexKey);
  // Чат, включенный самой партией (ИИ-матч, см. START_GAME): в отличие от
  // переписки агентов в разработке, он есть и в прод-сборке — у игроков,
  // зрителей и при просмотре записи
  const isMatchChatEnabled = useSelector(selectIsMatchChatEnabled);
  // Время хода вышло, сервер вот-вот закроет ход (см. turnInputLock): панели
  // действий на это время не нажимаются (выглядят как обычно — см. Footer.css).
  // Чат, реакции и кнопка завершения хода лежат вне этих контейнеров
  const turnInputLockedClassName = useSelector(selectIsTurnInputLocked) ? 'footer_turn-input-locked' : '';
  const isAiMatch = useSelector(selectIsAiMatch);

  const [isFooterCollapsed, setIsFooterCollapsed] = useState(false);
  // Угол кнопок на десктопе: по его ширине панель записи матча встает правее
  const [desktopCornerElement, setDesktopCornerElement] = useState(null);
  useFieldCornerControlsWidth(desktopCornerElement);
  // На десктопе сворачивания нет: футер, свернутый на узком окне, при
  // расширении окна разворачивается — иначе он остался бы внизу без кнопки
  useEffect(() => {
    if (isDesktopLayout) {
      setIsFooterCollapsed(false);
    }
  }, [isDesktopLayout]);
  // Свернутый футер уезжает вниз ровно настолько, чтобы над краем экрана
  // остались его шапка и все, что над ней. Раньше сдвиг был числом (100% −
  // 252 px — под кнопку хода над футером); на десктопе кнопки над футером
  // нет, футер ниже 252 px, и «свернутый» он уезжал вверх. Сдвиг меряется по
  // факту и следит за сменой содержимого, пока футер свернут
  const footerRef = useRef(null);
  const [collapseShift, setCollapseShift] = useState(null);
  useLayoutEffect(() => {
    const footer = footerRef.current;
    if (!isFooterCollapsed || !footer) {
      setCollapseShift(null);
      return undefined;
    }
    const measure = () => {
      const heading = footer.querySelector('.footer-heading');
      if (!heading) {
        return;
      }
      const shift = footer.getBoundingClientRect().bottom - heading.getBoundingClientRect().bottom;
      setCollapseShift(Math.max(0, Math.round(shift)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(footer);
    return () => observer.disconnect();
  }, [isFooterCollapsed]);
  const [isContentEmpty, setIsContentEmpty] = useState(false);
  // Развернутая панель реакций должна перекрывать кнопки боковых панелей
  // (z-index 1000-1200): сам футер — stacking context с z-index 100, поэтому
  // поднимается весь футер классом app-footer_reactions-expanded (до 1250 —
  // ниже модалок ModalWindow 1300 и тостов 10000)
  // Так же поднимают угол чат, слои поля и редактор миссий; развернутой из
  // них бывает только одна (см. useCornerPanels)
  const {isAnyExpanded: isCornerPanelExpanded, getPanelProps: getCornerPanelProps} = useCornerPanels();

  const prevIsContentEmpty = usePrev(isContentEmpty);

  // Выбор ячейки для просмотра автоматически сбрасывается в МОМЕНТ
  // наступления хода пользователя (или параллельного этапа, где действуют все
  // игроки сразу) — футер должен освободиться под фазовый UI. Именно на
  // переходе (usePrev), а не по состоянию: инспекция теперь доступна и в свой
  // ход, и сбрасывать ее на каждом апдейте нельзя. Исключение — ход, который
  // сервер сейчас автоматически пропустит (та же проверка, что и у
  // TurnOverlay): фазовый UI все равно не появится, выбор сохраняем.
  // Одновременная фаза снабжения сетевой игры — тоже параллельный этап: там
  // проверяется авто-завершение СВОЕГО игрока, а без него (зритель, запись
  // матча, уже закончивший игрок) раздавать нечего и выбор тоже сохраняем
  const isParallelSupply = useSelector(selectIsParallelSupply);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  const isParallelTurn = checkIfParallelTurn(phase, warStage, isParallelSupply);
  const prevIsCurrentUserTurn = usePrev(isCurrentUserTurn);
  const prevIsParallelTurn = usePrev(isParallelTurn);
  // Начало своего хода — смена ключа (раунд, фаза, игрок), а не переход флага
  // «мой ход»: флаг может не падать между ходами. В локальной партии людей он
  // верен всегда, ход следующей фазы может достаться тому же игроку, а снабжение,
  // закрытое таймером, сразу передает ход первому в строительстве
  const round = useSelector(selectRound);
  const turnPlayerId = useSelector((state) => selectSupplyActingPlayer(state)?.id);
  const ownTurnKey = isCurrentUserTurn && turnPlayerId ? `${round}|${phase}|${turnPlayerId}` : null;
  const prevOwnTurnKey = usePrev(ownTurnKey);
  // Вход в одновременное снабжение отдельно: если он пришел одним коммитом
  // сразу после другого параллельного этапа, перехода isParallelTurn нет
  const prevIsParallelSupplyPhase = usePrev(isParallelSupplyPhase);
  // Ячейка, инспекция которой была снята входом в этап ближнего боя, — кандидат
  // на автопоказ SelectedUnitMelee. Нужна памятка в ref: в ЧУЖУЮ фазу войны
  // смена этапа и расчет боя приезжают отдельными сетевыми экшенами (разными
  // коммитами) — на входе в этап pendingMeleeDamage еще пуст, а когда расчет
  // доезжает, инспекция уже сброшена, и эффекту автопоказа не за что зацепиться
  const combatAutoSelectHexRef = useRef(null);
  useEffect(() => {
    if (!inspectedHexKey) {
      return;
    }
    const turnJustStarted = Boolean(ownTurnKey) && ownTurnKey !== prevOwnTurnKey;
    const parallelTurnJustStarted = (isParallelTurn && !prevIsParallelTurn)
      || (isParallelSupplyPhase && !prevIsParallelSupplyPhase);
    if (!turnJustStarted && !parallelTurnJustStarted) {
      return;
    }
    if (isParallelSupplyPhase) {
      const supplyPlayer = selectSupplyActingPlayer(store.getState());
      if (!supplyPlayer || checkIfTurnWillBeAutoCompleted(store, supplyPlayer.id)) {
        return;
      }
    } else if (turnJustStarted && !isParallelTurn && checkIfTurnWillBeAutoCompleted(store)) {
      return;
    }
    if (phase === PHASES.WAR && warStage === WAR_PHASE_STAGES.COMBAT) {
      combatAutoSelectHexRef.current = inspectedHexKey;
    }
    dispatch(clearInspectedHex());
  }, [
    inspectedHexKey, ownTurnKey, prevOwnTurnKey, isParallelTurn, prevIsParallelTurn,
    isParallelSupplyPhase, prevIsParallelSupplyPhase, phase, warStage, dispatch,
  ]);

  // Смена ЭТАПА внутри собственной фазы войны (найм -> перемещение -> ...)
  // тоже освобождает футер под фазовый UI. Только в свой ход: в чужую фазу
  // войны выбор ячейки должен переживать смену этапов (эффект выше и так
  // покрывает вход в параллельный этап ближнего боя)
  const prevWarStage = usePrev(warStage);
  useEffect(() => {
    if (!inspectedHexKey || phase !== PHASES.WAR) {
      return;
    }
    if (!prevWarStage || !warStage || warStage === prevWarStage) {
      return;
    }
    if (!isCurrentUserTurn || !prevIsCurrentUserTurn) {
      return;
    }
    dispatch(clearInspectedHex());
  }, [inspectedHexKey, phase, warStage, prevWarStage, isCurrentUserTurn, prevIsCurrentUserTurn, dispatch]);

  // Отряд на инспектируемой ячейке вступил в бой: выстрелил (появился его
  // pending-выстрел) или участвует в рассчитанном ближнем бое — инспекция
  // сменяется на сам отряд, чтобы футер сразу показал расчет урона
  // (SelectedUnitShooting/SelectedUnitMelee), не дожидаясь клика пользователя
  const units = useSelector(selectUnits);
  const pendingShootingActions = useSelector(selectPendingShootingActions);
  const pendingMeleeDamage = useSelector(selectPendingMeleeDamage);
  useEffect(() => {
    // Вне этапа ближнего боя памятка о снятой на его входе инспекции не нужна
    if (phase !== PHASES.WAR || warStage !== WAR_PHASE_STAGES.COMBAT) {
      combatAutoSelectHexRef.current = null;
    }
    if (phase !== PHASES.WAR) {
      return;
    }
    let combatUnitOnInspectedHex = null;
    if (warStage === WAR_PHASE_STAGES.SHOOTING && inspectedHexKey) {
      combatUnitOnInspectedHex = units?.find((unit) => unit.hexKey === inspectedHexKey
        && pendingShootingActions?.some((action) => action.shooterUnitId === unit.id));
    } else if (warStage === WAR_PHASE_STAGES.COMBAT) {
      // Живая инспекция (свой ход: этап и расчет боя приходят одним коммитом)
      // либо памятка о снятой на входе в этап (чужой ход: расчет доезжает
      // отдельным сетевым экшеном уже после сброса инспекции)
      const candidateHexKey = inspectedHexKey || combatAutoSelectHexRef.current;
      if (candidateHexKey) {
        combatUnitOnInspectedHex = units?.find((unit) => unit.hexKey === candidateHexKey
          && pendingMeleeDamage?.some((action) => action.unitId === unit.id));
      }
    }
    if (combatUnitOnInspectedHex) {
      combatAutoSelectHexRef.current = null;
      if (inspectedHexKey) {
        dispatch(clearInspectedHex());
      }
      dispatch(selectUnit(combatUnitOnInspectedHex.id));
    }
  }, [inspectedHexKey, phase, warStage, units, pendingShootingActions, pendingMeleeDamage, dispatch]);

  // Автодеселект «протекшего» выбора отряда: смена этапа войны не сбрасывает
  // selectedUnitId (только смена игрока), и на поле оставалась бы подсветка
  // ячейки отряда, которому на новом этапе выбор уже ничего не дает — чужого
  // стрелка после завершения этапа стрельбы, своего отряда, выбранного на
  // перемещении, но не попавшего в ближний бой, и любого отряда без права
  // на маневр на этапе маневра
  const hexes = useSelector(selectHexes);
  useEffect(() => {
    if (!selectedUnitId || phase !== PHASES.WAR) {
      return;
    }
    const selectedUnit = units?.find((unit) => unit.id === selectedUnitId);
    if (!selectedUnit) {
      return;
    }
    if (warStage === WAR_PHASE_STAGES.COMBAT) {
      // Судим только когда расчет боя уже пришел: в чужой ход он приезжает
      // отдельным сетевым экшеном после смены этапа, и пустой pendingMeleeDamage
      // означает «еще не знаем состав боя», а не «отряд не участвует»
      if (pendingMeleeDamage?.length > 0
        && !pendingMeleeDamage.some((action) => action.unitId === selectedUnitId)
      ) {
        dispatch(deselectUnit());
      }
      return;
    }
    if (warStage === WAR_PHASE_STAGES.MANEUVER) {
      const actingPlayer = getActingPlayerFromState(store.getState());
      if (!actingPlayer || selectedUnit.ownerId !== actingPlayer.id
        || !checkIfUnitCouldManeuverFromHex(actingPlayer, selectedUnit, hexes)
      ) {
        dispatch(deselectUnit());
      }
    }
  }, [selectedUnitId, phase, warStage, units, pendingMeleeDamage, hexes, dispatch]);

  useEffect(() => {
    if (!isContentEmpty && prevIsContentEmpty) {
      setIsFooterCollapsed(false);
    }
  }, [isContentEmpty, prevIsContentEmpty, setIsFooterCollapsed]);

  // Собственный ход строительства после плана в чужой ход: футер все это
  // время не пустел (панель плана), и переход «контента не было -> появился»
  // выше не случается — свернутый на время плана футер разворачиваем явно
  useEffect(() => {
    if (phase === PHASES.CONSTRUCTION && isCurrentUserTurn && prevIsCurrentUserTurn === false) {
      setIsFooterCollapsed(false);
    }
  }, [phase, isCurrentUserTurn, prevIsCurrentUserTurn, setIsFooterCollapsed]);

  // Инспекция ячейки разворачивает свернутый футер принудительно. Эффекта
  // выше для этого мало: он ловит только переход «контента не было -> появился»,
  // а при клике по ячейке из фазы с непустым футером контент просто сменяется
  // на InspectedCellPanel — перехода нет. Развернуть вручную в этот момент
  // тоже нечем: кнопка разворота при инспекции не отображается (см. ниже)
  useEffect(() => {
    if (inspectedHexKey) {
      setIsFooterCollapsed(false);
    }
  }, [inspectedHexKey]);

  // Выбор казармы под найм (обратный порядок действий на этапе найма, см.
  // GameScene) — по той же причине: игроку нужно увидеть выбор типа отряда,
  // ради которого он казарму и выбрал
  useEffect(() => {
    if (selectedBarracksHexKey) {
      setIsFooterCollapsed(false);
    }
  }, [selectedBarracksHexKey]);

  useEffect(() => {
    if (gameResults) {
      setIsContentEmpty(false);
    }
  }, [gameResults]);

  const toggleCollapse = () => {
    REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
    setIsFooterCollapsed(!isFooterCollapsed)
  };

  // Результат стрельбы выбранного отряда показывается с попаданием залпа, а
  // не с выстрелом: иначе панель закрывает отряд-цель, по которому идет
  // анимация. Решение одно на заголовок, тулбар и футер зрителя
  const isShootingResultRevealed = useShootingResultRevealed();
  // Расчет урона ближнего боя — после ударов: пока на поле идет анимация,
  // итога боя нет ни на плашках отрядов, ни в футере. Признак включает
  // условие показа расчета (этап боя, выбран отряд-участник, не идет выбор
  // приоритетной цели): по нему же рисуется крестик снятия выбора
  const isMeleeDamageDetailsShown = useMeleeResultRevealed();
  const title = useFooterTitle({isShootingResultRevealed, isMeleeResultRevealed: isMeleeDamageDetailsShown});

  const activateDestructionTool = () => {
    dispatch(toggleDestructionTool());
  };

  const isPreparation = checkIfAnyGamePreparationPhase(phase);

  // В стрельбе и ближнем бою с выбранным отрядом на плите футера — картина
  // сражения (см. .footer-container_plate-battle в Footer.css)
  const isBattleBackground = phase === PHASES.WAR
    && (warStage === WAR_PHASE_STAGES.SHOOTING || warStage === WAR_PHASE_STAGES.COMBAT)
    && Boolean(selectedUnitId);

  // Режим зрителя (сетевой зритель, все-боты, реплей): футер с тулбаром и его
  // шапкой скрыт, остается только кнопка завершения фазы. Контейнер футера
  // прижат к низу (position: fixed; bottom: 0), поэтому без footer-container
  // кнопка сама опускается к нижнему краю экрана — большого пустого отступа
  // не остается. Исключения — инспекция ячейки и просмотр отряда, который
  // выстрелил/участвует в ближнем бою (тот же авто-выбор отряда по
  // инспектируемой ячейке, что и выше): их контент показывается в обычном
  // контейнере футера (только он: фазовый UI, реакции и прочие элементы
  // управления зрителю не принадлежат)
  if (isSpectatorMode) {
    // Результат стрельбы выбранного отряда — с попаданием его залпа (см.
    // useShootingResultRevealed): до этого контейнер футера зрителю не нужен
    const hasVisibleShootingContent = isShootingResultRevealed;
    const hasVisibleMeleeContent = isMeleeDamageDetailsShown;
    const showFooterContainer = inspectedHexKey || hasVisibleShootingContent || hasVisibleMeleeContent;

    // Угол зрителя: переписка (за матчами агентов наблюдают именно так; в
    // локальной разработке — всегда, см. ChatPanel, в прод-сборке — только
    // если чат включила сама партия) и слои поля
    const spectatorCornerControls = (
      <>
        {(process.env.NODE_ENV === 'development' || isMatchChatEnabled) && (
          <ChatPanel {...getCornerPanelProps(CORNER_PANELS.CHAT)}/>
        )}
        <VisualToggles {...getCornerPanelProps(CORNER_PANELS.LAYERS)}/>
      </>
    );

    return (<>
      <footer className={`app-footer ${addClassNameIfTrue(isCornerPanelExpanded, "app-footer_reactions-expanded")} ${cityCaptureHiddenClassName}`}>
        <div className={'header-above-controls-container'}>
          {!isDesktopLayout && (
            <div className={'field-corner-controls'} data-toasts-avoid={true}>{spectatorCornerControls}</div>
          )}
          {!isDesktopLayout && <CompletePhaseButton isSpectatorMode={true}/>}
        </div>

        {showFooterContainer && (
          <div className={`footer-container ${inspectingClassName}`} data-field-inset={'bottom'} data-toasts-avoid={true}>
            <FooterPlate/>
            <div className={'footer-heading'}>
              <div className={'footer-heading-description'}>
                {inspectedHexKey ? 'Выбранная ячейка' : title}
              </div>

              <InfoButton/>
              {inspectedHexKey && <DeselectButton/>}
              {isMeleeDamageDetailsShown && (
                <DeselectButton action={deselectUnit} title={'Снять выделение с отряда'}/>
              )}
            </div>

            <TokensToolbar
              setIsContentEmpty={setIsContentEmpty}
              isShootingResultRevealed={isShootingResultRevealed}
              isMeleeResultRevealed={isMeleeDamageDetailsShown}
            />
          </div>
        )}
      </footer>
      {isDesktopLayout && (
        <div ref={setDesktopCornerElement} data-toasts-avoid={true} className={`field-corner-controls field-corner-controls_desktop ${
          addClassNameIfTrue(isCornerPanelExpanded, 'field-corner-controls_expanded')
        } ${cityCaptureHiddenClassName}`}>
          {spectatorCornerControls}
        </div>
      )}
      {isDesktopLayout && (
        <div className={`complete-phase-corner ${cityCaptureHiddenClassName}`}>
          <CompletePhaseButton isSpectatorMode={true}/>
        </div>
      )}
    </>);
  }

  const isLocalDevelopmentChatEnabled = isEnvConfEnabled(
    ENV_CONFIG_VARIABLES.REACT_APP_LOCAL_CHAT_ENABLED
  );

  // Сводка найма и кнопки хода: на десктопе — колонкой над кнопкой завершения
  // в правом нижнем углу, на телефоне — слева от нее над футером
  const turnControls = (
    <div className={`elements-and-policy-button-wrapper ${turnInputLockedClassName}`}>
      <RecruitmentSummary/>

      {/* При планировании строительства в чужой ход — сама проверяет
          контекст планирования. Временно скрыта флагом в конфиге */}
      {IS_CONSTRUCTION_PLAN_AUTO_COMPLETE_TURN_BUTTON_SHOWN && <ConstructionPlanAutoCompleteTurnButton/>}

      <PolicyFanaticismConversionButton/>

      <ElementalsConstructionTokensOverlay/>

      <GiantsAutoSupplyToggle/>
    </div>
  );

  // Левый нижний угол: редактор миссий, чат, реакции, слои поля. На телефоне
  // — над футером слева, на десктопе — в углу окна вне футера: его высота и
  // transform при сворачивании угол не двигают
  const cornerControls = (
    <>
      {/* Панель редактора миссий занимает то же место, что панель реакций
          (в редакторе матч всегда локальный, поэтому вместе они не
          встречаются) и так же поднимает свой угол при разворачивании */}
      <MissionEditorPanel {...getCornerPanelProps(CORNER_PANELS.MISSION_EDITOR)}/>
      {/* Переписка игроков в локальной разработке (через нее
          договариваются LLM-агенты, играющие по MCP). Проверка вынесена
          сюда, а не внутрь компонента: сборка заменяет NODE_ENV литералом,
          и в прод-бандле эта часть условия удаляется как недостижимая.
          Чат, включенный самой партией (модерируемый чат ИИ-матча), —
          в любой сборке */}
      {((process.env.NODE_ENV === 'development' && isLocalDevelopmentChatEnabled) || isMatchChatEnabled) && (
        <ChatPanel {...getCornerPanelProps(CORNER_PANELS.CHAT)}/>
      )}
      {/* В матче с ИИ реакций нет: их панель слишком похожа на панель
          модерируемого чата рядом */}
      {!isAiMatch && <ReactionsPanel {...getCornerPanelProps(CORNER_PANELS.REACTIONS)}/>}
      <VisualToggles {...getCornerPanelProps(CORNER_PANELS.LAYERS)}/>
    </>
  );

  return <>
    <footer
      ref={footerRef}
      className={`app-footer ${
        addClassNameIfTrue(isFooterCollapsed, "collapsed")
      } ${
        addClassNameIfTrue(isCornerPanelExpanded, "app-footer_reactions-expanded")
      } ${cityCaptureHiddenClassName}`}
      style={collapseShift !== null ? {'--footer-collapse-shift': `${collapseShift}px`} : undefined}
    >
      <div className={'header-above-controls-container'}>
        {!isDesktopLayout && <div className={'field-corner-controls'} data-toasts-avoid={true}>{cornerControls}</div>}

        {!isDesktopLayout && <>
          {turnControls}
          <CompletePhaseButton/>
        </>}
      </div>

      <div className={`footer-container ${turnInputLockedClassName} ${inspectingClassName}`} data-field-inset={'bottom'} data-toasts-avoid={true}>
        <FooterPlate isBattle={isBattleBackground}/>
        <div className={'footer-heading'}>
          <div className={'footer-heading-description'}>
            {/* Пока идёт реплика, в шапке футера имя говорящего, а не подсказка
                текущей фазы: играть всё равно нельзя, а реплику должен кто-то
                «подписывать» */}
            {activeReplica
              ? (activeReplica.characterName || 'Реплика')
              : (inspectedHexKey ? 'Выбранная ячейка' : title)}
          </div>

          {inspectedHexKey ? (
            <>
              <InfoButton/>
              <DeselectButton/>
            </>
          ) : (
            <>
              {phase === PHASES.FIELD_PREPARATION && (
                <GameButton
                  variant={GAME_BUTTON_VARIANTS.SUNK}
                  size={GAME_BUTTON_SIZES.ICON}
                  pressed={destructionToolActive}
                  onClick={activateDestructionTool}
                  className={'footer-heading-btn'}
                >
                  <MaskedIcon
                    src={'/img/icons/hammer.png'}
                    alt={'Activate destruction tool'}
                    style={{opacity: destructionToolActive ? 1 : 0.5}}
                  />
                </GameButton>
              )}

              {!isPreparation && !gameResults ?
                <>
                  {/* В чужой ход при планировании строительства — отмена только
                      в плане: настоящая отмена задела бы строения ходящего игрока */}
                  {/* Пропуск хода отрядом на этапах войны — рядом с отменой, как и
                      остальные действия над отрядом (change unit-auto-selection) */}
                  {isCurrentUserTurn && <SkipUnitTurnButton/>}
                  {isCurrentUserTurn ? <UndoButton/> : (isConstructionPlanning && <PlanUndoButton/>)}
                  {/* Описание выбранного строения справа от списка — по запросу:
                      нужно новичку, опытному только занимает место */}
                  {phase === PHASES.CONSTRUCTION && (isCurrentUserTurn || isConstructionPlanning) && (
                    <BuildingHintsButton/>
                  )}
                  <InfoButton/>
                  {/* Перехват инициативы — только в свой ход: в чужой нажать ее
                      нельзя, а кто перехватил, видно в списке игроков */}
                  {isCurrentUserTurn && (
                    <InitiativeInterceptionButton/>
                  )}
                  {isMeleeDamageDetailsShown && (
                    <DeselectButton action={deselectUnit} title={'Снять выделение с отряда'}/>
                  )}
                </> : null
              }
            </>
          )}

          {/* В обучении кнопка сворачивания футера скрыта: подсказки и панели
              действий должны оставаться на виду весь сценарий. На десктопе ее
              нет совсем: футер в углу, и поля вокруг него хватает */}
          {!isDesktopLayout && !isContentEmpty && !inspectedHexKey && !isTutorialActive ?
            <GameButton
              variant={GAME_BUTTON_VARIANTS.SUNK}
              size={GAME_BUTTON_SIZES.ICON}
              onClick={toggleCollapse}
              className={`footer-heading-btn footer-heading_expand-button ${
                addClassNameIfTrue(isFooterCollapsed, 'footer-heading_expand-button-collapsed')
              }`}
            >
              <MaskedIcon
                src={'/img/icons/arrow down.png'}
                alt={'collapse'}
                tone={MASKED_ICON_TONES.CONTROLS}
              />
            </GameButton> : null
          }
        </div>

        {/* Пока персонаж говорит, тулбары прячем: играть всё равно нельзя,
            а текст реплики занимает контент футера целиком.
            После матча в футере итоги, но выбранная ячейка их вытесняет: поле
            остается на экране, и разбирают его именно осмотром ячеек. Таблица
            возвращается, как только выбор сняли (крестик в шапке футера) */}
        {activeReplica ? <MissionReplicaContent/> : (gameResults && !inspectedHexKey ?
          <GameEndContent isCompactMode={true}/>
          : <TokensToolbar
            setIsContentEmpty={setIsContentEmpty}
            isShootingResultRevealed={isShootingResultRevealed}
            isMeleeResultRevealed={isMeleeDamageDetailsShown}
          />
        )}
      </div>
    </footer>
    {/* Десктоп: кнопка хода (и сводка найма с кнопками хода над ней) — в
        правом нижнем углу окна, а не над футером: футер стоит слева от этой
        колонки, и высота его кнопку не двигает.
        На телефоне кнопка по-прежнему над футером и ездит вместе с ним —
        иначе при свернутом футере она висела бы посреди экрана */}
    {isDesktopLayout && (
      <div ref={setDesktopCornerElement} data-toasts-avoid={true} className={`field-corner-controls field-corner-controls_desktop ${
        addClassNameIfTrue(isCornerPanelExpanded, 'field-corner-controls_expanded')
      } ${cityCaptureHiddenClassName}`}>
        {cornerControls}
      </div>
    )}
    {isDesktopLayout && (
      <div className={`complete-phase-corner ${cityCaptureHiddenClassName}`}>
        {turnControls}
        <CompletePhaseButton/>
      </div>
    )}
  </>;
}